import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { CreditCard, CheckCircle, Loader2 } from 'lucide-react';
import { formatDate } from '@/lib/utils';

// Row display statuses are derived client-side the same way the backend
// derives them: paid | locked (not yet due) | due/overdue (payable).
const rowState = (row) => {
  if (row.status === 'paid') return 'paid';
  const now = new Date().toISOString();
  return row.due_date > now ? 'locked' : 'due';
};

export const CoursePaymentPlanCard = ({
  enrollment,
  onPayInstallment,        // (enrollment, seq) => void — omit to hide pay buttons
  payingInstallment,       // `${enrollment_id}_${seq}` while a checkout is starting
  renderRowAction,         // optional (enrollment, row) => node — admin mark-paid
}) => {
  const plan = enrollment.payment_plan;
  if (!plan || plan.type !== 'manual_monthly') return null;
  const rows = plan.installments || [];
  const paidCount = rows.filter((r) => r.status === 'paid').length;
  const remaining = rows
    .filter((r) => r.status !== 'paid')
    .reduce((sum, r) => sum + (r.total || 0), 0);
  const firstDue = rows.find((r) => rowState(r) === 'due');

  return (
    <Card className="border-slate-100" data-testid={`course-plan-${enrollment.enrollment_id}`}>
      <CardContent className="p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-[#0D1B2A]">
              {enrollment.course_title || 'Course'} — Monthly Plan
            </h3>
            <p className="text-sm text-slate-500">
              {plan.deposit_ex_vat > 0 && <>Deposit £{plan.deposit_ex_vat.toFixed(2)} ex VAT paid · </>}
              {paidCount} of {plan.num_months} paid · £{remaining.toFixed(2)} remaining
            </p>
          </div>
          {paidCount === rows.length ? (
            <Badge className="bg-emerald-100 text-emerald-700">Completed</Badge>
          ) : (
            <Badge className="bg-sky-100 text-sky-700">Active</Badge>
          )}
        </div>

        <div className="space-y-2">
          {rows.map((row) => {
            const state = rowState(row);
            const payKey = `${enrollment.enrollment_id}_${row.seq}`;
            return (
              <div
                key={row.seq}
                className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
              >
                <div className="flex items-center gap-3">
                  {state === 'paid' ? (
                    <CheckCircle className="w-4 h-4 text-emerald-600" />
                  ) : (
                    <CreditCard className="w-4 h-4 text-slate-400" />
                  )}
                  <span className="text-sm text-slate-700">
                    Instalment {row.seq} · £{(row.total || 0).toFixed(2)}
                  </span>
                  <span className="text-xs text-slate-400">
                    Due {formatDate(row.due_date)}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {state === 'paid' && (
                    <Badge className="bg-emerald-100 text-emerald-700">Paid</Badge>
                  )}
                  {state === 'locked' && (
                    <Badge className="bg-slate-100 text-slate-500">Upcoming</Badge>
                  )}
                  {state === 'due' && onPayInstallment && firstDue && row.seq === firstDue.seq && (
                    <Button
                      size="sm"
                      className="bg-[#0D1B2A] hover:bg-[#1B3A4B]"
                      disabled={payingInstallment === payKey}
                      onClick={() => onPayInstallment(enrollment, row.seq)}
                      data-testid={`pay-installment-${payKey}`}
                    >
                      {payingInstallment === payKey ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        'Pay now'
                      )}
                    </Button>
                  )}
                  {state === 'due' && !onPayInstallment && (
                    <Badge className="bg-amber-100 text-amber-700">Due</Badge>
                  )}
                  {renderRowAction && renderRowAction(enrollment, row)}
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

export default CoursePaymentPlanCard;
